<template>
  <div class="search-view">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ t('search.title') }}</h1>
        <p class="page-sub" v-if="q">{{ t('search.for') }} “{{ q }}”</p>
      </div>
    </div>

    <div v-if="loading" class="spinner"></div>

    <div v-else-if="!q" class="empty-state">
      <p>{{ t('search.empty') }}</p>
    </div>

    <div v-else-if="!tasks.length && !projects.length && !clients.length" class="empty-state">
      <p>{{ t('search.noResults') }} “{{ q }}”</p>
    </div>

    <div v-else class="result-groups">
      <!-- Tasks -->
      <section v-if="tasks.length" class="rgroup">
        <h2 class="rgroup-title">{{ t('search.tasks') }} <span class="rgroup-count">{{ tasks.length }}</span></h2>
        <router-link
          v-for="task in tasks"
          :key="'t' + task.id"
          :to="task.project_id ? `/tasks?project_id=${task.project_id}` : '/tasks'"
          class="rrow"
        >
          <span class="rrow-main">{{ task.title }}</span>
          <span class="rrow-meta">
            <span v-if="task.project_name" class="rrow-tag">{{ task.project_name }}</span>
            <span class="status-pill" :class="'st-' + task.status">{{ t('status.' + task.status) }}</span>
          </span>
        </router-link>
      </section>

      <!-- Projects -->
      <section v-if="projects.length" class="rgroup">
        <h2 class="rgroup-title">{{ t('search.projects') }} <span class="rgroup-count">{{ projects.length }}</span></h2>
        <router-link
          v-for="p in projects"
          :key="'p' + p.id"
          :to="`/tasks?project_id=${p.id}`"
          class="rrow"
        >
          <span class="rrow-main">
            <span class="rrow-dot" :style="{ background: p.color }"></span>{{ p.name }}
          </span>
          <span class="rrow-meta"><span class="rrow-tag">{{ p.task_count ?? 0 }} {{ t('search.tasks') }}</span></span>
        </router-link>
      </section>

      <!-- Clients -->
      <section v-if="clients.length" class="rgroup">
        <h2 class="rgroup-title">{{ t('search.clients') }} <span class="rgroup-count">{{ clients.length }}</span></h2>
        <router-link
          v-for="c in clients"
          :key="'c' + c.id"
          to="/clients"
          class="rrow"
        >
          <span class="rrow-main">{{ c.name }}</span>
          <span class="rrow-meta"><span class="rrow-tag">{{ c.username }}</span></span>
        </router-link>
      </section>
    </div>
  </div>
</template>

<script>
import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from '@/composables/useI18n'
import api from '@/api'

export default {
  name: 'SearchView',
  setup() {
    const route = useRoute()
    const { t } = useI18n()
    const q = ref('')
    const loading = ref(false)
    const tasks = ref([])
    const projects = ref([])
    const clients = ref([])

    function matches(text, term) {
      return (text || '').toLowerCase().includes(term)
    }

    async function run() {
      q.value = (route.query.q || '').toString().trim()
      tasks.value = []; projects.value = []; clients.value = []
      if (!q.value) return
      const term = q.value.toLowerCase()
      loading.value = true
      try {
        // Tasks: server-side query. Projects + clients: small datasets, filter locally.
        const [tr, pr, cr] = await Promise.all([
          api.getTasks({ q: q.value }),
          api.getProjects(),
          api.getClients().catch(() => ({ data: [] })),
        ])
        tasks.value = tr.data || []
        projects.value = (pr.data || []).filter(p => matches(p.name, term))
        clients.value = (cr.data || []).filter(c => matches(c.name, term) || matches(c.username, term))
      } catch {} finally {
        loading.value = false
      }
    }

    watch(() => route.query.q, run, { immediate: true })

    return { t, q, loading, tasks, projects, clients }
  },
}
</script>

<style scoped>
.page-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }
.result-groups { display: flex; flex-direction: column; gap: 24px; }
.rgroup-title {
  font-size: 13px; font-weight: 700; color: var(--ink, var(--text));
  text-transform: uppercase; letter-spacing: .04em; margin-bottom: 10px;
  display: flex; align-items: center; gap: 8px;
}
.rgroup-count {
  font-variant-numeric: tabular-nums; font-size: 11px; font-weight: 600; color: var(--muted);
  background: var(--surface-2); border: 1px solid var(--border); border-radius: 999px; padding: 1px 8px;
}
.rrow {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 11px 14px; border: 1px solid var(--border); border-radius: var(--radius-sm, 8px);
  background: var(--surface); text-decoration: none; color: var(--text);
  margin-bottom: 6px; transition: background .12s, border-color .12s;
}
.rrow:hover { background: var(--hover); border-color: var(--accent); }
.rrow-main { font-size: 13.5px; font-weight: 500; display: flex; align-items: center; gap: 8px; min-width: 0; }
.rrow-dot { width: 9px; height: 9px; border-radius: 3px; flex-shrink: 0; background: var(--accent); }
.rrow-meta { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.rrow-tag { font-size: 11px; color: var(--muted); }
.status-pill {
  font-size: 10.5px; font-weight: 600; padding: 2px 9px; border-radius: 999px;
  background: var(--surface-2); color: var(--muted); border: 1px solid var(--border);
}
</style>
